// MARK: JPB Final Report (single, formal report)
//
// Builds the full Word document directly with the `docx` library — no template
// patching. The document follows the v3 layout (subject ejendom block,
// headline price, "Forudsætninger" parameter tables, transactions table,
// disclaimer) and pulls in the best v1/v2 visualisations: subject map,
// comparison map (with JPB legend overlay), histogram, statistics-card snapshot,
// polygon-analyzer-card snapshot (with "Sådan beregnes" expanded),
// jordbund overview map (Bilag 2), and a price/area heatmap (Bilag 1).

import {
   Document,
   Packer,
   Paragraph,
   TextRun,
   ImageRun,
   Table,
   TableRow,
   TableCell,
   HeadingLevel,
   AlignmentType,
   WidthType,
   BorderStyle,
   PageBreak,
   PageOrientation,
   SectionType,
   Footer,
   PageNumber
} from "docx";
import {
   AreaUnit,
   AverageCalculationMethod,
   JPBAssumptions,
   JPBReferenceProperty,
   JPBReportData
} from "./JPBState";
import {
   calculateEffectiveMarkSquareMeterPrice,
   formatArea,
   formatCurrency,
   formatDate,
   formatIndexDifferenceValue,
   formatPercentage,
   formatPricePerSquareMeter,
   getNonAgricultureToAgricultureRatio
} from "./JPBFunc";
import { AddressInterface } from "src/managers/AddressInterface";

// -----------------------------------------------------------------------------
// MARK: Image helpers
// -----------------------------------------------------------------------------

async function getImageSizeFromBytes(bytes: Uint8Array): Promise<{ width: number; height: number } | undefined> {
   return new Promise((resolve) => {
      const blob = new Blob([bytes as any], { type: "image/png" });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
         const width = img.naturalWidth;
         const height = img.naturalHeight;
         URL.revokeObjectURL(url);
         resolve({ width, height });
      };
      img.onerror = () => {
         URL.revokeObjectURL(url);
         resolve(undefined);
      };
      img.src = url;
   });
}

async function getImageTransformation(
   bytes: Uint8Array | undefined,
   targetWidth = 600
): Promise<{ width: number; height: number } | undefined> {
   if (!bytes) return undefined;
   const size = await getImageSizeFromBytes(bytes);
   if (!size?.width || !size?.height) return undefined;
   const ratio = size.height / size.width;
   return { width: targetWidth, height: Math.round(targetWidth * ratio) };
}

async function imageOrFallback(
   bytes: Uint8Array | undefined,
   fallback: string,
   targetWidth = 600
): Promise<Paragraph> {
   const transformation = await getImageTransformation(bytes, targetWidth);
   if (!bytes || !transformation) {
      return new Paragraph({
         alignment: AlignmentType.CENTER,
         children: [new TextRun({ text: fallback, italics: true, color: "808080" })]
      });
   }
   return new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
         new ImageRun({ type: "png", data: bytes, transformation } as any)
      ]
   });
}

// -----------------------------------------------------------------------------
// MARK: Formatting helpers
// -----------------------------------------------------------------------------

function triggerDownload(blob: Blob, filename: string) {
   const docxUrl = URL.createObjectURL(blob);
   const a = document.createElement("a");
   a.href = docxUrl;
   a.download = filename;
   a.click();
   URL.revokeObjectURL(docxUrl);
}

function formatShortDate(date: Date): string {
   const dd = String(date.getDate()).padStart(2, "0");
   const mm = String(date.getMonth() + 1).padStart(2, "0");
   const yyyy = date.getFullYear();
   return `${dd}.${mm}.${yyyy}`;
}

function formatNumber(value: number, fractionDigits: number): string {
   return value.toLocaleString("da-DK", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
   });
}

function formatTransactionType(value: string): string {
   return value
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase())
      .replace(/\s+/g, " ")
      .trim();
}

/** Format a soil-quality factor (0..1+) as an "indeks" number, e.g. 0.925 → "92,5". */
function formatSoilIndex(factor: number): string {
   return formatNumber(factor * 100, factor * 100 % 1 === 0 ? 0 : 1);
}

function formatAreaBoth(areaM2: number | undefined): string {
   if (!areaM2 || !isFinite(areaM2)) return "-";
   const ha = areaM2 / 10000;
   return `${formatNumber(ha, 4)} ha (${formatNumber(areaM2, 0)} m²)`;
}

/** Split comma-separated BFE-nr string into individual non-empty entries. */
function splitBfeNumbers(bfe: string | null | undefined): string[] {
   if (!bfe) return [];
   return String(bfe)
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
}

// -----------------------------------------------------------------------------
// MARK: Address resolution for Bilag 3 (transactions table)
// -----------------------------------------------------------------------------

const ADDRESS_RESOLVE_ATTEMPTS = 3;
const ADDRESS_RETRY_DELAY_MS = 300;

function delay(ms: number): Promise<void> {
   return new Promise((resolve) => setTimeout(resolve, ms));
}

async function resolveAddressWithRetry(lat: number, lng: number): Promise<string> {
   let lastError: unknown;
   for (let attempt = 1; attempt <= ADDRESS_RESOLVE_ATTEMPTS; attempt++) {
      try {
         return await AddressInterface.resolvePropertyInfoStyleAddressFromPoint(lat, lng) || "";
      } catch (error) {
         lastError = error;
         if (attempt < ADDRESS_RESOLVE_ATTEMPTS) {
            await delay(ADDRESS_RETRY_DELAY_MS * attempt);
         }
      }
   }
   console.warn("Failed to resolve JPB report address after retries", { lat, lng, error: lastError });
   return "";
}

/**
 * Resolves a DAWA-style address for each given coordinate, returning a Map
 * from "lat,lng" key → address (empty string if unknown).
 * Calls run in parallel; transient failures are retried before falling back to
 * empty strings.
 */
async function resolveAddressesParallel(
   coords: Array<{ key: string; lat: number; lng: number }>
): Promise<Map<string, string>> {
   const out = new Map<string, string>();
   if (coords.length === 0) return out;
   const results = await Promise.all(
      coords.map(async (c) => {
         const addr = await resolveAddressWithRetry(c.lat, c.lng);
         return { key: c.key, address: addr };
      })
   );
   for (const r of results) out.set(r.key, r.address);
   return out;
}

// -----------------------------------------------------------------------------
// MARK: Table cell helpers
// -----------------------------------------------------------------------------

type AlignmentValue = (typeof AlignmentType)[keyof typeof AlignmentType];

const HEADER_SHADING = "D9E2F3";
const ROW_BORDER_COLOR = "BFBFBF";

// Small breathing-room padding inside every table cell. Values are in dxa
// (twentieths of a point). 100 dxa ≈ 5pt ≈ 6.7px on a typical 96-DPI render.
const CELL_MARGINS = { top: 80, bottom: 80, left: 100, right: 100 } as const;

function singleBorder() {
   const b = { style: BorderStyle.SINGLE, size: 4, color: ROW_BORDER_COLOR } as const;
   return { top: b, bottom: b, left: b, right: b };
}

function paragraph(text: string, opts: { bold?: boolean; alignment?: AlignmentValue; italics?: boolean } = {}): Paragraph {
   return new Paragraph({
      alignment: opts.alignment ?? AlignmentType.LEFT,
      children: [new TextRun({ text, bold: opts.bold, italics: opts.italics })]
   });
}

function multilineParagraphs(lines: string[], alignment: AlignmentValue = AlignmentType.LEFT): Paragraph[] {
   if (lines.length === 0) return [paragraph("-", { alignment })];
   return lines.map((l) => paragraph(l, { alignment }));
}

function headerCell(text: string, alignment: AlignmentValue = AlignmentType.LEFT, widthPct?: number): TableCell {
   return new TableCell({
      width: widthPct ? { size: widthPct, type: WidthType.PERCENTAGE } : undefined,
      shading: { fill: HEADER_SHADING },
      borders: singleBorder(),
      margins: CELL_MARGINS,
      children: [paragraph(text, { bold: true, alignment })]
   });
}

function bodyCell(text: string, alignment: AlignmentValue = AlignmentType.LEFT, opts: { bold?: boolean; widthPct?: number } = {}): TableCell {
   return new TableCell({
      width: opts.widthPct ? { size: opts.widthPct, type: WidthType.PERCENTAGE } : undefined,
      borders: singleBorder(),
      margins: CELL_MARGINS,
      children: [paragraph(text, { bold: opts.bold, alignment })]
   });
}

function multilineBodyCell(lines: string[], alignment: AlignmentValue = AlignmentType.LEFT, opts: { widthPct?: number } = {}): TableCell {
   return new TableCell({
      width: opts.widthPct ? { size: opts.widthPct, type: WidthType.PERCENTAGE } : undefined,
      borders: singleBorder(),
      margins: CELL_MARGINS,
      children: multilineParagraphs(lines, alignment)
   });
}

// -----------------------------------------------------------------------------
// MARK: Subject ejendom block (non-table, formal "title-page" layout)
// -----------------------------------------------------------------------------

// Mapit Viamap brand green (matches `--viamapGreen` in mitGlassDesign.css).
const ACCENT_COLOR = "334B49";

/** Centered horizontal divider (a thin colored line). */
function horizontalRule(): Paragraph {
   return new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 120, after: 120 },
      border: {
         bottom: { style: BorderStyle.SINGLE, size: 6, color: ACCENT_COLOR, space: 1 }
      },
      children: [new TextRun({ text: "" })]
   });
}

/**
 * Builds the subject-ejendom block as a borderless, centered 2-column table so
 * that all labels right-align in one column and all values left-align in the
 * adjacent column — a clean "definition list" / grid layout.
 */
function buildSubjectInfoBlock(data: JPBReportData): Table {
   const polygonAreaM2 = data.subjectAreaM2 ?? data.polygonAnalysis?.polygonAreaM2;
   const subjectAddress = data.polygonAnalysis?.subjectAddress?.trim() || "-";
   const matr = data.subjectMatr?.trim() || "-";
   const rows: Array<{ label: string; value: string }> = [
      { label: "Adresse",  value: subjectAddress },
      { label: "Kommune",  value: data.subjectKommune?.trim() || "-" },
      { label: "BFE-nr.",  value: data.subjectBfe?.trim() || "-" },
      { label: "Matr.nr.", value: matr },
      { label: "Areal",    value: formatAreaBoth(polygonAreaM2) }
   ];

   const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" } as const;
   const noBorders = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };

   const labelCell = (text: string) => new TableCell({
      width: { size: 30, type: WidthType.PERCENTAGE },
      borders: noBorders,
      margins: { top: 30, bottom: 30, left: 60, right: 120 },
      children: [new Paragraph({
         alignment: AlignmentType.RIGHT,
         children: [new TextRun({ text: text.toUpperCase(), bold: true, color: "7F7F7F", size: 18 })]
      })]
   });

   const valueCell = (text: string) => new TableCell({
      width: { size: 70, type: WidthType.PERCENTAGE },
      borders: noBorders,
      margins: { top: 30, bottom: 30, left: 0, right: 60 },
      children: [new Paragraph({
         alignment: AlignmentType.LEFT,
         children: [new TextRun({ text, size: 24 })]
      })]
   });

   return new Table({
      // Center the table itself horizontally on the page, and let it size to
      // ~70% of the content area so the grid sits as a tidy block.
      alignment: AlignmentType.CENTER,
      width: { size: 70, type: WidthType.PERCENTAGE },
      borders: {
         top: noBorder, bottom: noBorder, left: noBorder, right: noBorder,
         insideHorizontal: noBorder, insideVertical: noBorder
      },
      rows: rows.map((r) => new TableRow({
         children: [labelCell(r.label), valueCell(r.value)]
      }))
   });
}

// -----------------------------------------------------------------------------
// MARK: Forudsætninger — split into two tables
//   1. Filter parameters: which transactions are included in the comparison set
//   2. Calculation parameters: how the price is computed from those transactions
// -----------------------------------------------------------------------------

type ParameterRow = { parameter: string; value: string; meaning: string };

function buildFilterParameterRows(data: JPBReportData): ParameterRow[] {
   const a: JPBAssumptions = data.assumptions;
   const dateFrom = data.calculationDateFrom ? formatShortDate(new Date(data.calculationDateFrom)) : "-";
   const dateTo = data.calculationDateTo ? formatShortDate(new Date(data.calculationDateTo)) : "-";

   const bufferKm = data.bufferDistanceKm ?? data.searchRadiusKm;
   const bufferText = bufferKm === undefined
      ? "-"
      : bufferKm <= 0
         ? "Ingen buffer (0 km)"
         : `${formatNumber(bufferKm, 1)} km`;

   const geomKindLabel = ((): string => {
      switch (data.sourceLayerGeometryKind) {
         case "point": return "punkt";
         case "multipoint": return "multipunkt";
         case "line": return "linje";
         case "polygon": return "polygon";
         case "multipolygon": return "multipolygon";
         case "mixed": return "blandet";
         default: return "geometri";
      }
   })();
   const featureCount = data.sourceFeatureCount;
   const sourcePart = featureCount && featureCount > 0
      ? `${featureCount} ${geomKindLabel}${featureCount > 1 ? "er" : ""}`
      : geomKindLabel;
   const sourceLayerText = data.sourceLayerName
      ? `${data.sourceLayerName} (${sourcePart})`
      : sourcePart;

   const searchAreaText = data.searchAreaKm2 !== undefined
      ? `${formatNumber(data.searchAreaKm2, data.searchAreaKm2 < 10 ? 2 : 1)} km²`
      : "-";

   const minMarkPct = `${a.effectiveMarkOverlapThreshold}%`;

   const minAreaM2 = Math.max(0, a.areaMin ?? 0);
   const minAreaText = `${formatNumber(minAreaM2 / 10000, minAreaM2 / 10000 % 1 === 0 ? 0 : 2)} ha (${formatNumber(minAreaM2, 0)} m²)`;
   const maxAreaM2 = Math.max(0, a.areaMax ?? 0);
   const maxAreaText = `${formatNumber(maxAreaM2 / 10000, maxAreaM2 / 10000 % 1 === 0 ? 0 : 2)} ha (${formatNumber(maxAreaM2, 0)} m²)`;

   const buildingMinM2 = Math.max(0, a.buildingAreaOkM2 ?? 0);
   const buildingMinText = buildingMinM2 < 1000
      ? `${formatNumber(buildingMinM2, 0)} m²`
      : `${formatNumber(buildingMinM2 / 10000, 1)} ha (${formatNumber(buildingMinM2, 0)} m²)`;

   const priceIntervalText = `${formatNumber(a.pricePerMeterMin, 0)} – ${formatNumber(a.pricePerMeterMax, 0)} kr/m² (${formatNumber(a.pricePerMeterMin * 10000, 0)} – ${formatNumber(a.pricePerMeterMax * 10000, 0)} kr/ha)`;

   const transactionTypesText = (a.includeTransactionTypes ?? [])
      .map(formatTransactionType)
      .join(", ") || "-";

   const buildingsIncluded = a.excludeBuildings === true ? "Uden" : "Med";

   return [
      {
         parameter: "Analyseret areal",
         value: sourceLayerText,
         meaning: "Det kortlag (punkter, linjer eller polygoner) der definerer udgangspunktet for søgningen efter sammenligningshandler."
      },
      {
         parameter: "Buffer omkring arealet",
         value: bufferText,
         meaning: "Sammenligningshandler søges indenfor denne afstand fra arealet. Sættes typisk til 0 km hvis arealet i sig selv er en stor polygon (fx en kommune)."
      },
      {
         parameter: "Samlet søgeområde",
         value: searchAreaText,
         meaning: "Det samlede areal hvori sammenligningshandler er fundet (det analyserede areal udvidet med bufferen)."
      },
      {
         parameter: "Periode",
         value: `${dateFrom} – ${dateTo}`,
         meaning: "Tidsinterval for de medtagne handler."
      },
      {
         parameter: "Minimumsareal",
         value: minAreaText,
         meaning: "Mindste arealmæssige størrelse på handler der medregnes."
      },
      {
         parameter: "Maksimumsareal",
         value: maxAreaText,
         meaning: "Største arealmæssige størrelse på handler der medregnes."
      },
      {
         parameter: "Minimum andel af marker på ejendommen",
         value: minMarkPct,
         meaning: "Ejendomme, hvor andelen af marker på ejendommen er under denne grænse, medtages ikke."
      },
      {
         parameter: "Bygninger i handler",
         value: buildingsIncluded,
         meaning: "Om handler med bygninger overhovedet indgår i analysen."
      },
      {
         parameter: "Minimum handlet jord ved bygninger",
         value: buildingMinText,
         meaning: "Handler med bygninger, hvor der er handlet mindre jordareal end denne grænse, medregnes ikke."
      },
      {
         parameter: "Prisinterval",
         value: priceIntervalText,
         meaning: "Sammenligningshandler udenfor det fastsatte prisinterval medregnes ikke."
      },
      {
         parameter: "Transaktionstype",
         value: transactionTypesText,
         meaning: "Andre transaktionstyper for handler indgår ikke (fx mageskifte, familieintern handel mm.)."
      }
   ];
}

function buildCalculationParameterRows(data: JPBReportData): ParameterRow[] {
   const a: JPBAssumptions = data.assumptions;
   const indexed = data.applyIndexCalculation === true;
   const indexLabel = data.priceIndexSourceLabel?.trim() || "Mølbaks Jordprisindeks";

   const weightingValue = (data.averageCalculationMethod ?? AverageCalculationMethod.WeightedByArea) === AverageCalculationMethod.WeightedByArea
      ? "Vægtet"
      : "Ikke vægtet";

   const nonAgriPct = Math.round(getNonAgricultureToAgricultureRatio(a) * 100);

   // JB 5-6 is the reference jordtype; its actual indeks value is user-configurable
   // (defaults to 100 but can be set to anything). All other JB-indekser are
   // expressed relative to this value.
   const jb56IndexValue = formatSoilIndex(a.soilQualityFactors.JB_5_6);
   const jb56Ref = `reference-jordtypen JB 5-6 (sat til indeks ${jb56IndexValue})`;

   return [
      {
         parameter: "Vægtning",
         value: weightingValue,
         meaning: "Angiver om det gennemsnitlige prisniveau beregnes som et vægtet gennemsnit efter de handlede arealers størrelse."
      },
      {
         parameter: "Indeksering",
         value: indexed ? `Ja (${indexLabel})` : "Nej",
         meaning: indexed
            ? "Handelsprisen er diskonteret til nutidsværdi med det valgte jordprisindeks."
            : "Handelspriserne er ikke diskonteret til nutidsværdi."
      },
      {
         parameter: "Værdi af ikke-marker",
         value: `${nonAgriPct}%`,
         meaning: "Ikke-marker (skov, eng, naturarealer mm.) værdisættes til den angivne andel af markens værdi."
      },
      {
         parameter: "Indeks JB 1-2",
         value: formatSoilIndex(a.soilQualityFactors.JB_1_2),
         meaning: `Værdiforhold for JB 1-2 i forhold til ${jb56Ref}.`
      },
      {
         parameter: "Indeks JB 3-4",
         value: formatSoilIndex(a.soilQualityFactors.JB_3_4),
         meaning: `Værdiforhold for JB 3-4 i forhold til ${jb56Ref}.`
      },
      {
         parameter: "Indeks JB 5-6",
         value: jb56IndexValue,
         meaning: "Reference-jordtype. Anvendes som udgangspunkt for de øvrige JB-indekser."
      },
      {
         parameter: "Indeks JB 7-10",
         value: formatSoilIndex(a.soilQualityFactors.JB_7_10),
         meaning: `Værdiforhold for JB 7-10 i forhold til ${jb56Ref}.`
      },
      {
         parameter: "Indeks JB 11",
         value: formatSoilIndex(a.soilQualityFactors.JB_11),
         meaning: `Værdiforhold for JB 11 i forhold til ${jb56Ref}.`
      }
   ];
}

function buildParameterTable(rows: ParameterRow[]): Table {
   const headerRow = new TableRow({
      tableHeader: true,
      children: [
         headerCell("Parameter", AlignmentType.LEFT, 28),
         headerCell("Brugervalgt indstilling", AlignmentType.LEFT, 28),
         headerCell("Betydning", AlignmentType.LEFT, 44)
      ]
   });

   const dataRows = rows.map((r) => new TableRow({
      children: [
         bodyCell(r.parameter, AlignmentType.LEFT, { bold: true }),
         bodyCell(r.value),
         bodyCell(r.meaning)
      ]
   }));

   return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...dataRows]
   });
}

// -----------------------------------------------------------------------------
// MARK: Transactions table (Bilag 3) — landscape, BFE per line, resolved address
// -----------------------------------------------------------------------------

function buildTransactionsTable(
   data: JPBReportData,
   areaUnit: AreaUnit,
   addressByKey: Map<string, string>
): Table {
   const checked = data.referenceProperties.filter((p) => data.checkedDocumentIds.includes(p.documentID));
   const nonAgricultureRatio = getNonAgricultureToAgricultureRatio(data.assumptions);

   const isHectare = areaUnit === AreaUnit.Hectare;
   const areaHeader = `Areal (${isHectare ? "ha" : "m²"})`;
   const priceHeader = `Jordpris (indekseret) pr. ${isHectare ? "ha" : "m²"}`;
   const effectiveHeader = `Effektiv markpris pr. ${isHectare ? "ha" : "m²"}`;

   // Tuned column widths for landscape: address gets the most room, BFE has a
   // narrow column with one BFE-nr per line, and numeric columns are compact.
   // The new "#" column is taken from the address slot to keep the total at 100 %.
   const W_INDEX = 4;
   const W_ADDR = 14;
   const W_BFE = 9;
   const W_DATE = 9;
   const W_HANDELSPRIS = 10;
   const W_INDEKS = 8;
   const W_JORDPRIS = 10;
   const W_AREAL = 8;
   const W_EFFEKTIV = 10;
   const W_MARK = 8;

   const headerRow = new TableRow({
      tableHeader: true,
      children: [
         headerCell("#", AlignmentType.RIGHT, W_INDEX),
         headerCell("Adresse", AlignmentType.LEFT, W_ADDR),
         headerCell("BFE-nr.", AlignmentType.LEFT, W_BFE),
         headerCell("Handelsdato", AlignmentType.LEFT, W_DATE),
         headerCell("Handelspris", AlignmentType.RIGHT, W_HANDELSPRIS),
         headerCell("Indeksforskel", AlignmentType.RIGHT, W_INDEKS),
         headerCell(priceHeader, AlignmentType.RIGHT, W_JORDPRIS),
         headerCell(areaHeader, AlignmentType.RIGHT, W_AREAL),
         headerCell(effectiveHeader, AlignmentType.RIGHT, W_EFFEKTIV),
         headerCell("Mark-%", AlignmentType.RIGHT, W_MARK)
      ]
   });

   const dataRows = checked.map((p: JPBReferenceProperty, rowIdx: number) => {
      const effectivePrice = calculateEffectiveMarkSquareMeterPrice(p, nonAgricultureRatio);

      const key = `${p.coordinates.lat},${p.coordinates.lng}`;
      const resolvedAddress = (p.address && p.address.trim()) || addressByKey.get(key) || "-";

      const bfeLines = splitBfeNumbers(p.bfe_nr);
      const bfeForCell = bfeLines.length > 0 ? bfeLines : ["-"];

      return new TableRow({
         children: [
            bodyCell(String(rowIdx + 1), AlignmentType.RIGHT, { widthPct: W_INDEX, bold: true }),
            bodyCell(resolvedAddress, AlignmentType.LEFT, { widthPct: W_ADDR }),
            multilineBodyCell(bfeForCell, AlignmentType.LEFT, { widthPct: W_BFE }),
            bodyCell(formatDate(p.transactionDate), AlignmentType.LEFT, { widthPct: W_DATE }),
            bodyCell(formatCurrency(p.totalPrice, 0), AlignmentType.RIGHT, { widthPct: W_HANDELSPRIS }),
            bodyCell(formatIndexDifferenceValue(p.indexDifferencePct), AlignmentType.RIGHT, { widthPct: W_INDEKS }),
            bodyCell(formatPricePerSquareMeter(p.pricePerSquareMeter, areaUnit), AlignmentType.RIGHT, { widthPct: W_JORDPRIS }),
            bodyCell(formatArea(p.area, areaUnit), AlignmentType.RIGHT, { widthPct: W_AREAL }),
            bodyCell(effectivePrice !== null ? formatPricePerSquareMeter(effectivePrice, areaUnit) : "-", AlignmentType.RIGHT, { widthPct: W_EFFEKTIV }),
            bodyCell(formatPercentage(p.overlapArea?.markOverlap), AlignmentType.RIGHT, { widthPct: W_MARK })
         ]
      });
   });

   return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...dataRows]
   });
}

// -----------------------------------------------------------------------------
// MARK: Headings & spacers
// -----------------------------------------------------------------------------

function h1(text: string): Paragraph {
   return new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 320, after: 160 },
      children: [new TextRun({ text })]
   });
}

function h2(text: string): Paragraph {
   return new Paragraph({
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 240, after: 120 },
      children: [new TextRun({ text })]
   });
}

function p(text: string, opts: { bold?: boolean; italics?: boolean; alignment?: AlignmentValue } = {}): Paragraph {
   return new Paragraph({
      alignment: opts.alignment ?? AlignmentType.LEFT,
      spacing: { after: 120 },
      children: [new TextRun({ text, bold: opts.bold, italics: opts.italics })]
   });
}

function spacer(): Paragraph {
   return new Paragraph({ children: [new TextRun({ text: "" })] });
}

function pageBreak(): Paragraph {
   return new Paragraph({ children: [new PageBreak()] });
}

/**
 * Footer that displays "Side X af Y" centered at the bottom of every page.
 * `PageNumber.CURRENT` and `PageNumber.TOTAL_PAGES` are special docx field
 * children that Word resolves at render time.
 */
function buildPageNumberFooter(): Footer {
   return new Footer({
      children: [
         new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
               new TextRun({ text: "Side ", size: 18, color: "7F7F7F" }),
               new TextRun({ children: [PageNumber.CURRENT], size: 18, color: "7F7F7F" }),
               new TextRun({ text: " af ", size: 18, color: "7F7F7F" }),
               new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 18, color: "7F7F7F" })
            ]
         })
      ]
   });
}

// -----------------------------------------------------------------------------
// MARK: Main entry point
// -----------------------------------------------------------------------------

const DISCLAIMER_TEXT = "Beregningen er baseret på brugerens valgte parametre i jordprisanalysen. Resultatet kan derfor variere efter den enkelte brugers valg. Beregningen er alene baseret på tidligere handler og tager således ikke højde for forhold, der fremover kan få betydning for prisdannelsen i markedet, som fx ejendommens beliggenhed i forhold til kvælstofretention eller grundvandsdannende oplande. Beregningen tager heller ikke højde for fx tinglyste servitutter på ejendommen, ligesom beregningen ikke tager højde for andre forhold, som kan have indflydelse på værdiansættelsen, som fx en vandingstilladelse eller andre særlige forhold på ejendommen. Det er således brugeren – og ikke systemleverandøren – der fastlægger forudsætningerne for analysen, og brugeren der dermed påtager sig ansvaret for resultatet af beregningen.";

// Standard A4 dimensions in dxa (twentieths of a point) — always pass PORTRAIT
// dimensions; the docx library swaps width/height internally when orientation
// is LANDSCAPE (see PageSize constructor in docx@9).
const A4_W = 11906;
const A4_H = 16838;

export async function handleGenerateJPBRapport(data: JPBReportData): Promise<void> {
   if (!data.polygonAnalysis) {
      throw new Error("JPB report requires polygon analysis result");
   }

   const analysis = data.polygonAnalysis;
   const reportDate = data.reportDate ?? new Date();
   const userEmail = data.userEmail?.trim() || "-";
   const haPrice = formatPricePerSquareMeter(analysis.estimatedPricePerSquareMeter, AreaUnit.Hectare);
   const m2Price = formatPricePerSquareMeter(analysis.estimatedPricePerSquareMeter, AreaUnit.SquareMeter);
   const numHandler = data.checkedDocumentIds.length;
   const subjectAddress = analysis.subjectAddress?.trim() || "-";

   // Pre-render images (so we can embed them in paragraphs).
   const subjectMapBytes = data.subjectPolygonImage;
   const comparisonMapBytes = data.allPointsMapImage;
   const histogramBytes = data.histogramImage;
   const statisticsCardBytes = data.statisticsCardImage;
   const polygonAnalyzerBytes = data.polygonAnalyzerImage;
   const jordbundMapBytes = data.overviewWithJordbundImage;
   const heatmapBytes = data.heatmapImage;
   // The title-page subject map is rendered slightly narrower than the body
   // images so it sits naturally inside the title-page composition while still
   // filling the available width below the headline result.
   const TITLE_PAGE_MAP_WIDTH = 540;

   // Resolve missing transaction addresses in parallel before building Bilag 3.
   const checkedProps = data.referenceProperties.filter((rp) => data.checkedDocumentIds.includes(rp.documentID));
   const coordsToResolve = checkedProps
      .filter((rp) => !rp.address || !rp.address.trim())
      .filter((rp) => !!rp.coordinates && Number.isFinite(rp.coordinates.lat) && Number.isFinite(rp.coordinates.lng))
      .map((rp) => ({ key: `${rp.coordinates.lat},${rp.coordinates.lng}`, lat: rp.coordinates.lat, lng: rp.coordinates.lng }));
   // De-duplicate by key so we don't fire identical requests.
   const uniqueByKey = new Map<string, { key: string; lat: number; lng: number }>();
   for (const c of coordsToResolve) if (!uniqueByKey.has(c.key)) uniqueByKey.set(c.key, c);

   const [
      subjectMapParagraph,
      comparisonMapParagraph,
      histogramParagraph,
      statisticsCardParagraph,
      polygonAnalyzerParagraph,
      jordbundMapParagraph,
      heatmapParagraph,
      addressByKey
   ] = await Promise.all([
      imageOrFallback(subjectMapBytes, "[Kort over analyseret areal ikke tilgængeligt]", TITLE_PAGE_MAP_WIDTH),
      imageOrFallback(comparisonMapBytes, "[Kort over sammenligningshandler ikke tilgængeligt]"),
      imageOrFallback(histogramBytes, "[Søjlediagram ikke tilgængeligt]"),
      imageOrFallback(statisticsCardBytes, "[Statistik ikke tilgængelig]"),
      imageOrFallback(polygonAnalyzerBytes, "[Polygonanalyse ikke tilgængelig]"),
      imageOrFallback(jordbundMapBytes, "[Jordbundskort ikke tilgængeligt]"),
      imageOrFallback(heatmapBytes, "[Heatmap ikke tilgængeligt]"),
      resolveAddressesParallel(Array.from(uniqueByKey.values()))
   ]);

   // ---- Portrait section: title page → forudsætninger → per-jordbund → Bilag 1–2
   const portraitChildren: Array<Paragraph | Table> = [];

   // 1. ---- Formal title page (LaTeX-style) -------------------------------------
   //    Single divider between the subject info and the headline result; the
   //    rest of the page relies on whitespace/typography for separation. The
   //    subject map is embedded on the same page so the reader sees the
   //    location together with the headline result.
   portraitChildren.push(
      // Top label
      new Paragraph({
         alignment: AlignmentType.CENTER,
         spacing: { before: 400, after: 60 },
         children: [
            new TextRun({ text: "MAPIT JORDPRISBEREGNER", bold: true, color: "7F7F7F", size: 18, characterSpacing: 40 })
         ]
      }),
      // Big title
      new Paragraph({
         heading: HeadingLevel.TITLE,
         alignment: AlignmentType.CENTER,
         spacing: { before: 0, after: 60 },
         children: [new TextRun({ text: "Jordprisanalyse", size: 60, bold: true, color: ACCENT_COLOR })]
      }),
      // Subtitle
      new Paragraph({
         alignment: AlignmentType.CENTER,
         spacing: { before: 0, after: 200 },
         children: [
            new TextRun({ text: "Værdiansættelse af landbrugsareal på baggrund af sammenligningshandler", italics: true, color: "595959", size: 22 })
         ]
      }),

      // "Vedrørende ejendom" header
      new Paragraph({
         alignment: AlignmentType.CENTER,
         spacing: { before: 80, after: 60 },
         children: [
            new TextRun({ text: "VEDRØRENDE EJENDOM", bold: true, color: "7F7F7F", size: 18, characterSpacing: 40 })
         ]
      }),
      buildSubjectInfoBlock(data),

      // Single divider — separates the metadata block above from the headline
      // result below.
      horizontalRule(),

      // Headline result label
      new Paragraph({
         alignment: AlignmentType.CENTER,
         spacing: { before: 160, after: 60 },
         children: [
            new TextRun({ text: "ESTIMERET JORDPRIS", bold: true, color: "7F7F7F", size: 20, characterSpacing: 40 })
         ]
      }),
      // HUGE result — the single most important number in the entire report.
      new Paragraph({
         alignment: AlignmentType.CENTER,
         spacing: { before: 40, after: 0 },
         children: [
            new TextRun({ text: `${haPrice}`, bold: true, size: 76, color: ACCENT_COLOR })
         ]
      }),
      new Paragraph({
         alignment: AlignmentType.CENTER,
         spacing: { before: 0, after: 40 },
         children: [
            new TextRun({ text: "pr. hektar", size: 22, color: "595959" })
         ]
      }),
      new Paragraph({
         alignment: AlignmentType.CENTER,
         spacing: { before: 60, after: 160 },
         children: [
            new TextRun({ text: `(svarende til ${m2Price}/m²)`, italics: true, size: 20, color: "595959" }),
            new TextRun({ text: "    ·    ", size: 20, color: "BFBFBF" }),
            new TextRun({ text: "Beregnet på baggrund af ", size: 20, color: "595959" }),
            new TextRun({ text: String(numHandler), bold: true, size: 20, color: "595959" }),
            new TextRun({ text: " sammenligningshandler", size: 20, color: "595959" })
         ]
      }),

      // Subject map — kept on the title page so the reader sees the location
      // alongside the headline result.
      subjectMapParagraph,
      new Paragraph({
         alignment: AlignmentType.CENTER,
         spacing: { before: 40, after: 200 },
         children: [
            new TextRun({ text: subjectAddress, italics: true, size: 18, color: "595959" })
         ]
      }),

      // Metadata footer of the title page
      new Paragraph({
         alignment: AlignmentType.CENTER,
         spacing: { before: 0, after: 0 },
         children: [
            new TextRun({ text: "Udarbejdet ", size: 18, color: "7F7F7F" }),
            new TextRun({ text: formatShortDate(reportDate), bold: true, size: 18, color: "7F7F7F" }),
            new TextRun({ text: " af ", size: 18, color: "7F7F7F" }),
            new TextRun({ text: userEmail, bold: true, size: 18, color: "7F7F7F" })
         ]
      }),

      // ---- End of title page ----
      pageBreak()
   );

   // 4. Forudsætninger — split into filter + calculation tables.
   //    The title page above already ends with a `pageBreak()`, so we must NOT
   //    emit another one here — that would leave a blank page in between.
   portraitChildren.push(
      h1("Forudsætninger for beregning af jordprisen"),
      p("Forudsætningerne nedenfor er delt i to: parametre der bestemmer hvilke handler der inddrages som sammenligningsgrundlag, og parametre der bestemmer selve beregningen af jordprisen."),
      h2("Parametre for udvalg af sammenligningshandler"),
      p("Disse parametre afgør hvilke nabohandler der overhovedet indgår i analysen."),
      buildParameterTable(buildFilterParameterRows(data)),
      spacer(),
      h2("Parametre for beregning af jordprisen"),
      p("Disse parametre afgør hvordan jordprisen beregnes ud fra de udvalgte sammenligningshandler."),
      buildParameterTable(buildCalculationParameterRows(data)),
      spacer()
   );

   // 5. Sammenligningshandler i området
   const sectionBufferKm = data.bufferDistanceKm ?? data.searchRadiusKm;
   const sectionAreaText = data.searchAreaKm2 !== undefined
      ? `${formatNumber(data.searchAreaKm2, data.searchAreaKm2 < 10 ? 2 : 1)} km²`
      : undefined;
   const sectionBufferText = sectionBufferKm === undefined
      ? "i søgeområdet defineret af det analyserede areal"
      : sectionBufferKm <= 0
         ? "inden for selve det analyserede areal (ingen buffer anvendt)"
         : `inden for en buffer på ${formatNumber(sectionBufferKm, 1)} km omkring det analyserede areal`;
   const sectionAreaSentence = sectionAreaText
      ? ` Det samlede søgeområde er ca. ${sectionAreaText}.`
      : "";
   portraitChildren.push(
      h2("Sammenligningshandler i området"),
      p(`Alle handler ${sectionBufferText} er fundet og vurderet ud fra ovennævnte forudsætninger.${sectionAreaSentence} Signaturforklaringen under kortet viser hvilke prikker der indgår i beregningen og hvilke der filtreres fra.`),
      comparisonMapParagraph,
      ...(sectionAreaText
         ? [p(`Samlet søgeområde: ${sectionAreaText}.`, { italics: true, alignment: AlignmentType.CENTER })]
         : []),
      spacer(),
      p("Søjlediagram med de valgte handler:"),
      histogramParagraph,
      spacer()
   );

   // 6. Per-jordbund-beregning (no JB index reference table — already in §4)
   portraitChildren.push(
      pageBreak(),
      h1("Beregning på baggrund af jordbundskvalitet"),
      p(`Med henblik på at kunne beregne arealprisen for marker og markernes jordbundskvalitet er der beregnet en korrigeret arealpris for markerne, samt indregnet det indbyrdes værdiforhold mellem jordbundskvaliteterne (referencen er JB 5-6 = indeks ${formatSoilIndex(data.assumptions.soilQualityFactors.JB_5_6)}). De anvendte JB-indekser fremgår af parametertabellen i forrige afsnit.`),
      p("Arealpriserne pr. jordbundskvalitet for de udvalgte sammenligningshandler:"),
      statisticsCardParagraph,
      p("De viste lav-/middel-/høj-værdier er et 50 %-interval omkring middelværdien (vægtet gennemsnit ± 0,674 · standardafvigelse). Under antagelse af, at arealpriserne er tilnærmelsesvis normalfordelte, ligger ca. halvdelen af handlerne inden for dette interval. Intervallet er beskrivende statistik, ikke et formelt konfidensinterval for middelværdien.", { italics: true }),
      spacer(),
      p(`På baggrund af ovennævnte arealpriser pr. jordbundskvalitet og under forudsætning af at ikke-marker værdisættes til ${Math.round(getNonAgricultureToAgricultureRatio(data.assumptions) * 100)}% af den gennemsnitlige markpris er arealprisen for det analyserede areal beregnet således:`),
      polygonAnalyzerParagraph,
      spacer()
   );

   // ---- Portrait section: Bilag 1 (Prisheatmap)
   //      `SectionType.NEXT_PAGE` already starts a new page — do NOT add a leading
   //      `pageBreak()` here or Word inserts a blank page (same fix as title page).
   const portraitBilag1Children: Array<Paragraph | Table> = [
      h1("Bilag 1 – Prisheatmap"),
      p("Kort over de valgte handler farvet efter pris pr. m² (gul = lav, rød = høj) og dimensioneret efter handlet areal. Signaturforklaringen til højre for kortet viser pris- og arealskalaerne."),
      heatmapParagraph,
      spacer(),
   ];

   // ---- Portrait section: Bilag 2 (Jordbundsforhold) — placed immediately before
   //      the transactions table so map row numbers can be cross-referenced.
   const portraitBilag2Children: Array<Paragraph | Table> = [
      h1("Bilag 2 – Jordbundsforhold"),
      p(`Kort over jordtyper i området sammen med de ${numHandler} valgte landbrugsjordhandler. Tallene ved hver prik svarer til rækkenumrene i Bilag 3 på næste side. Bestemte jordtyper er mere velegnede til landbrugsdrift end andre og resulterer derfor oftest i højere landbrugsjordpriser.`),
      jordbundMapParagraph,
      spacer(),
   ];

   // ---- Landscape section: Bilag 3 (transactions table) + disclaimer
   const landscapeBilag3Children: Array<Paragraph | Table> = [
      h1("Bilag 3 – Anvendte handler"),
      p(`Tabel med valgte handler efter indstillede parametre: ${numHandler} handler. Rækkenumrene i kolonnen "#" matcher tallene ved prikkerne på kortet i Bilag 2.`),
      buildTransactionsTable(data, AreaUnit.Hectare, addressByKey),
      spacer(),
      h1("Ansvarsfraskrivelse"),
      p(DISCLAIMER_TEXT),
   ];

   const doc = new Document({
      creator: userEmail,
      title: `Jordprisanalyse – ${subjectAddress}`,
      description: "Jordprisanalyse genereret af Mapit Jordprisberegner",
      styles: {
         default: {
            document: {
               run: { font: "Calibri", size: 22 }
            }
         },
         paragraphStyles: [
            {
               id: "Heading1",
               name: "Heading 1",
               basedOn: "Normal",
               next: "Normal",
               quickFormat: true,
               run: { size: 32, bold: true, color: ACCENT_COLOR },
               paragraph: { spacing: { before: 320, after: 160 } }
            },
            {
               id: "Heading2",
               name: "Heading 2",
               basedOn: "Normal",
               next: "Normal",
               quickFormat: true,
               // Subheading uses a slightly lighter shade of the brand green.
               run: { size: 26, bold: true, color: "5C7B78" },
               paragraph: { spacing: { before: 240, after: 120 } }
            },
            {
               id: "Title",
               name: "Title",
               basedOn: "Normal",
               next: "Normal",
               quickFormat: true,
               run: { size: 44, bold: true, color: ACCENT_COLOR },
               paragraph: { spacing: { before: 0, after: 240 } }
            }
         ]
      },
      sections: [
         {
            properties: {
               type: SectionType.NEXT_PAGE,
               page: {
                  size: { width: A4_W, height: A4_H, orientation: PageOrientation.PORTRAIT },
                  margin: { top: 1000, right: 1000, bottom: 1000, left: 1000 }
               }
            },
            footers: { default: buildPageNumberFooter() },
            children: portraitChildren
         },
         {
            properties: {
               type: SectionType.NEXT_PAGE,
               page: {
                  size: { width: A4_W, height: A4_H, orientation: PageOrientation.PORTRAIT },
                  margin: { top: 1000, right: 1000, bottom: 1000, left: 1000 }
               }
            },
            footers: { default: buildPageNumberFooter() },
            children: portraitBilag1Children
         },
         {
            properties: {
               type: SectionType.NEXT_PAGE,
               page: {
                  size: { width: A4_W, height: A4_H, orientation: PageOrientation.PORTRAIT },
                  margin: { top: 1000, right: 1000, bottom: 1000, left: 1000 }
               }
            },
            footers: { default: buildPageNumberFooter() },
            children: portraitBilag2Children
         },
         {
            properties: {
               type: SectionType.NEXT_PAGE,
               page: {
                  size: { width: A4_W, height: A4_H, orientation: PageOrientation.LANDSCAPE },
                  margin: { top: 800, right: 800, bottom: 800, left: 800 }
               }
            },
            footers: { default: buildPageNumberFooter() },
            children: landscapeBilag3Children
         }
      ]
   });

   const blob = await Packer.toBlob(doc);
   triggerDownload(blob, `Jordprisanalyse ${subjectAddress}.docx`);
}
